/**
 * Eseménynapló — a DIFF-ÉRTÉKEK lokalizálása (CC6 maradéka).
 *
 * A lelet: a napló mezőszintű diffje a nyers tárolt értéket írta ki, tehát a leggyakoribb változás
 * (`status`: `in_progress` → `resolved`, mérve 1627 jegy-sor) NYERS ENUM-KULCSKÉNT jelent meg, miközben
 * ugyanannak az értéknek a listákon és az adatlapokon rendes magyar címkéje van. A mező-NEVEK már
 * lokalizáltak voltak (`activityLog.field.*`), csak az ÉRTÉKEK nem — épp a beszédes felük.
 *
 * A megoldás DRY: nem új szótár, hanem a MÁR LÉTEZŐ modul-szótárak újrahasznosítása
 * (`tickets.statusLabel`, `meetings.locationLabel`, `assets.usageLabel`, …) — így egy címke-javítás egy
 * helyen hat, és nem csúszhat el a lista meg a napló szövege.
 *
 * A feloldás kulcsa a (DOMÉN, MEZŐ) pár: a domén az action első szegmense (`tickets.update_ticket` →
 * `tickets`), a mező a diff `field`-je. Ugyanaz a mező-név több doménben MÁST jelent (`status` a jegynél
 * életciklus, a találkozónál RSVP), ezért a doménre kötött tábla az egyetlen helyes forma — egy lapos
 * érték→címke szótár csendben összemosná őket.
 *
 * Ismeretlen (domén, mező) vagy hiányzó kulcs → a NYERS érték marad. Ez szándékos: a napló elsődleges
 * feladata a hiteles nyom, nem a szépség — sosem írunk oda kitalált címkét.
 */

/** A legacy MySQL `snake_case` mező-nevei és a natív `camelCase` alak UGYANARRA a szótárra képez. */
type FieldMap = Record<string, string>;

/**
 * (domén → mező → i18n kulcs-PREFIX). A prefix mögé a nyers érték kerül: `tickets.statusLabel.resolved`.
 * A prefixek MEGLÉVŐ modul-szótárakra mutatnak — a fal (`i18n/activity-log-action.wall.spec.ts`) ellenőrzi,
 * hogy mindegyik létezik hu+en oldalon is.
 */
const VALUE_LABEL_PREFIX: Record<string, FieldMap> = {
  tickets: {
    status: 'tickets.statusLabel',
    type: 'tickets.typeLabel',
  },
  // A találkozó `status`-a a RÉSZVÉTELI válasz (pending/confirmed/declined) — nem életciklus-státusz.
  meetings: {
    status: 'meetings.rsvp',
    location: 'meetings.locationLabel',
    zoom_account: 'meetings.zoomAccountLabel',
    zoomAccount: 'meetings.zoomAccountLabel',
  },
  assets: {
    status: 'assets.statusLabel',
    category: 'assets.categoryLabel',
    type: 'assets.typeLabel',
    usage_location: 'assets.usageLabel',
    usageLocation: 'assets.usageLabel',
  },
  clients: {
    status: 'clients.statusLabel',
  },
  accounts: {
    status: 'accounts.statusLabel',
    type: 'accounts.typeLabel',
  },
  // a fiók-domain állapota (aktív · setup alatt · inaktív) — NEM a fiók-státusz szótára (abban nincs setup)
  'account-domains': {
    status: 'accounts.domains.status',
  },
  contacts: {
    status: 'contacts.statusLabel',
  },
  employees: {
    status: 'employees.statusLabel',
  },
  messages: {
    status: 'messages.statusLabel',
    type: 'messages.typeLabel',
  },
  invoices: {
    status: 'invoices.statusLabel',
    type: 'invoices.typeLabel',
  },
  services: {
    status: 'serviceCatalog.statusLabel',
    category: 'serviceCatalog.categoryLabel',
  },
};

/**
 * A legacy BOOLEAN-szerű mezők: a MySQL-ből `'true'`/`'false'` (illetve `enabled`/`disabled`) STRINGKÉNT
 * jött át, tehát a `renderValue` ✓/✗ ága (ami igazi booleanre fut) nem éri el őket. Csak a MEZŐ-NÉV
 * alapján fordítunk — egy szabad-szöveges mezőben a „true" szó nem igen/nem.
 */
const BOOLEAN_FIELDS = new Set([
  'checked',
  'auto_billing',
  'autoBilling',
  'is_primary',
  'isPrimary',
  'notify_email',
  'notifyEmail',
  'notify_in_app',
  'notifyInApp',
  'active',
  'archived',
]);

const BOOLEAN_VALUE_KEY: Record<string, string> = {
  true: 'common.yes',
  false: 'common.no',
  '1': 'common.yes',
  '0': 'common.no',
  enabled: 'common.yes',
  disabled: 'common.no',
};

/** i18n-feloldó szűkítve arra, amit itt használunk (a `TFunction` generikus szigora nélkül). */
type Translate = (key: string, options: { defaultValue: string }) => string;

/**
 * Egy diff-érték lokalizált címkéje — vagy `null`, ha nincs (a hívó ilyenkor a nyerset írja ki).
 * Csak STRING értékre fut: szám/objektum/tömb nem enum.
 */
export function diffValueLabel(
  domain: string,
  field: string,
  value: unknown,
  t: Translate,
): string | null {
  if (typeof value !== 'string' || value === '') return null;

  if (BOOLEAN_FIELDS.has(field)) {
    const boolKey = BOOLEAN_VALUE_KEY[value.toLowerCase()];
    if (boolKey !== undefined) {
      const label = t(boolKey, { defaultValue: '' });
      return label === '' ? null : label;
    }
  }

  const prefix = VALUE_LABEL_PREFIX[domain]?.[field];
  if (prefix === undefined) return null;
  // Csak enum-ALAKÚ értékre kérdezünk rá (a szabad szöveg nem kulcs, és a pont kulcs-szeparátor is lenne).
  if (!/^[a-z0-9_+-]+$/i.test(value)) return null;
  const label = t(`${prefix}.${value}`, { defaultValue: '' });
  return label === '' ? null : label;
}

/** A napló-fal használja: a táblában szereplő ÖSSZES i18n kulcs-prefix (a létezésük ellenőrzéséhez). */
export const VALUE_LABEL_PREFIXES: readonly string[] = [
  ...new Set(Object.values(VALUE_LABEL_PREFIX).flatMap((m) => Object.values(m))),
].sort();

/** A napló-fal használja: a boolean-értékek i18n kulcsai. */
export const BOOLEAN_VALUE_KEYS: readonly string[] = [
  ...new Set(Object.values(BOOLEAN_VALUE_KEY)),
].sort();
